import React from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Единый вид карточек-метрик вкладки «Очередь» (Алексей 12.10, #1452 «причесать визуал,
 *  правильнее акценты, консистентнее»): подпись капителью с иконкой и (i), ОДНО главное
 *  число цветом, вторичные — серым в ряд с подписями под ними. Те же отступы у всех. */

export const METRIC_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

export function InfoHint({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <Tooltip content={<span className="block max-w-72 text-xs leading-snug">{text}</span>}>
      <span className="inline-flex size-4 shrink-0 cursor-help items-center justify-center text-muted-foreground/70 hover:text-foreground"><Info className="size-3.5" /></span>
    </Tooltip>
  );
}

export function MetricCard({ icon: Icon, title, hint, right, className, bodyClassName, children }: {
  icon?: React.ElementType;
  title: React.ReactNode;
  hint?: string;
  /** Справа в шапке — переключатель, вторичная метрика, кнопка. */
  right?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    // h-full: карточки одного ряда одной высоты — «неровная высота» хуже пустого
    // низа (Алексей 12.10, #1465).
    <Card className={cn('h-full', className)}>
      <CardContent className={cn('flex h-full flex-col px-4 pb-3 pt-3', bodyClassName)}>
        {/* Заголовок не обрезается: если правому блоку (переключатель, вторичная
            метрика) не хватает места, он переносится на следующую строку справа
            («РАЗМЕ…», «СК…» на трёх карточках в ряд — 12.10, #1457). */}
        <div className="mb-2 flex min-h-5 flex-wrap items-center gap-x-1.5 gap-y-1">
          {Icon && <Icon className="size-3.5 shrink-0 text-muted-foreground" />}
          <span className={cn(METRIC_TITLE, 'min-w-0')}>{title}</span>
          <InfoHint text={hint} />
          {right && <div className="ml-auto flex shrink-0 items-center gap-2">{right}</div>}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/** Главное число: крупно, цветом акцента; единица мельче рядом; подпись под ним. */
export function MetricBig({ value, unit, label, className, style }: {
  value: React.ReactNode; unit?: string; label: string; className?: string; style?: React.CSSProperties;
}) {
  return (
    <div className="min-w-0">
      <div className={cn('flex items-baseline gap-1 text-4xl font-semibold leading-none tabular-nums', className)} style={style}>
        <span>{value}</span>
        {unit && <span className="text-lg font-medium text-muted-foreground">{unit}</span>}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

/** Вторичное число: средне, серым; подпись под ним — тот же ритм, что у главного. */
export function MetricSmall({ value, unit, label, className }: { value: React.ReactNode; unit?: string; label: string; className?: string }) {
  return (
    <div className="min-w-0">
      <div className={cn('flex items-baseline gap-1 text-2xl font-semibold leading-none tabular-nums text-muted-foreground', className)}>
        <span>{value}</span>
        {unit && <span className="text-sm font-medium">{unit}</span>}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

/** Ряд чисел: главное и вторичные с одинаковыми промежутками. */
export function MetricRow({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-end gap-x-6 gap-y-2', className)}>{children}</div>;
}
